'use client'

import { play } from 'cuelume'
import { useAtomValue, useSetAtom, useStore } from 'jotai'
import { useTranslations } from 'next-intl'
import { useCallback, useMemo, useState } from 'react'

import {
  showAlgoLifecycleToast,
  showErrorToast,
  showOrderNotificationToast,
  showSecurityAlertToast,
  showSimpleNotificationToast,
} from '@/components/ui/notification-toast-helpers'
import { useAuth } from '@/features/auth/hooks'
import { tradeVenueToIconKey } from '@/lib/proto/util'
import { toast } from '@/lib/toast'
import { randomUUID } from '@/lib/utils'
import { useActiveAccountsScope, useUserAccounts } from '@/service/account.hook'
import {
  reconcileTerminalAlgoState,
  TERMINAL_ALGO_STATUSES,
} from '@/service/ws/account-order-merge'
import {
  classifyNotification,
  isSilentNotification,
  isVenueTelemetry,
  notificationTitleKey,
} from '@/service/ws/classify-notification'
import { createOrderNotificationLifecycle } from '@/service/ws/order-notification-lifecycle'
import { useMultiNotificationStream } from '@/service/ws/use-orders-stream'
import { algoStatesRawAtom } from '@/state/account'
import { activeOrderOpenRequestAtom } from '@/state/active-tab'
import { notificationHistoryRawAtom, pushNotification } from '@/state/notifications'
import {
  type OptimisticOrderResult,
  optimisticOrderResultAtomFamily,
  pendingOptimisticOrdersAtom,
  pendingOptimisticSymbolsAtom,
  pendingRfqOrdersAtom,
  targetKey,
} from '@/state/order'
import { activeRfqPopupIdsAtom } from '@/state/rfq'
import { soundSettingsAtom } from '@/state/storage'
import { applyVenueTelemetry, venueTelemetryAtom } from '@/state/venue-telemetry'
import { AlgoStatus } from '@/types/generated/algo'
import { OrderStatus } from '@/types/generated/common/trading'
import { ServiceNotification } from '@/types/generated/notifications'

/**
 * A fill/algo-completion notification older than this is treated as replayed
 * backlog (reconnect after the tab was hidden/backgrounded), not a live event.
 * It still gets recorded to history and toasted — only the SFX is skipped —
 * so a burst of backlog delivered at once doesn't stack overlapping audio.
 * Mirrors REJECTION_FRESH_MS in account-order-merge.ts.
 */
const SOUND_FRESH_MS = 10_000

/**
 * NotificationManager component
 * Handles background listening for order notifications, records every one to the
 * notification history, and displays toasts.
 *
 * Place this component globally (e.g., in layout or a root provider).
 */
export function NotificationManager() {
  const tNotifications = useTranslations('notifications')
  const { isAuthenticated } = useAuth()
  const { accountIds, isResolved, hasAccounts } = useActiveAccountsScope()
  // showDisabled: a notification can arrive for an account the user has since
  // disabled — still want the toast to name it rather than showing nothing.
  const { data: userAccountsData } = useUserAccounts({ showDisabled: true })
  const accountInfoById = useMemo(
    () =>
      new Map(
        (userAccountsData?.accounts ?? []).map((a) => [
          a.accountId,
          { name: a.name, exchange: tradeVenueToIconKey(a.tradeVenue) },
        ])
      ),
    [userAccountsData?.accounts]
  )
  const settings = useAtomValue(soundSettingsAtom)
  const pendingOrders = useAtomValue(pendingOptimisticOrdersAtom)
  const pendingSymbols = useAtomValue(pendingOptimisticSymbolsAtom)
  const pendingRfqs = useAtomValue(pendingRfqOrdersAtom)
  const store = useStore()

  // One batched payload instead of one per account. `hasAccounts` (not just
  // `accountIds.length`) gates this so an unresolved account list never
  // produces an accidental `accountIds: []` subscribe, which the backend
  // resolves as "every account the caller can read."
  const notificationPayloads = useMemo(
    () => (hasAccounts ? [{ accountId: '', accountIds }] : []),
    [hasAccounts, accountIds]
  )

  const setAlgoStates = useSetAtom(algoStatesRawAtom)
  const [orderNotificationLifecycle] = useState(createOrderNotificationLifecycle)

  const handleNotificationMessage = useCallback(
    (notification?: ServiceNotification) => {
      if (!notification) return
      if (isVenueTelemetry(notification)) {
        store.set(venueTelemetryAtom, (s) => applyVenueTelemetry(s, notification))
      }
      // Venue-layer state for the market-maker algo (see isSilentNotification): not for the
      // bell, the history panel or a toast.
      if (isSilentNotification(notification)) return

      const classified = classifyNotification(notification)
      const titleKey = notificationTitleKey(classified.title)
      const toastTitle = titleKey ? tNotifications(titleKey) : classified.title
      const accountInfo = accountInfoById.get(notification.accountId)
      const accountName = accountInfo?.name
      const exchange = accountInfo?.exchange
      const isFreshForSound = Date.now() - Number(notification.timestamp) <= SOUND_FRESH_MS
      store.set(notificationHistoryRawAtom, (prev) =>
        pushNotification(prev, {
          id: randomUUID(),
          source: 'service',
          timestamp: Number(notification.timestamp),
          accountId: notification.accountId,
          ...classified,
          raw: notification,
        })
      )

      const {
        orderResponse,
        algoLifecycle,
        orderValidationError,
        orderExecutionError,
        securityAlert,
        venueNotice,
      } = notification

      if (securityAlert) {
        showSecurityAlertToast(classified, notification.timestamp, accountName, exchange)
        return
      }

      if (venueNotice) {
        toast.warning(classified.message)
        return
      }

      // Market-maker venue layer: a dead-man switch that fired or couldn't arm, or a Derive
      // fill reverted at settlement. Each is the whole story, so a toast and done.
      if (notification.deadmanFired || notification.deadmanArmRefused) {
        showSimpleNotificationToast(
          classified.severity,
          toastTitle,
          undefined,
          accountName,
          exchange
        )
        if (classified.severity === 'error' && isFreshForSound) play('error')
        return
      }
      if (notification.tradeReverted) {
        showSimpleNotificationToast('error', toastTitle, classified.symbol, accountName, exchange)
        if (isFreshForSound) play('error')
        return
      }

      // ── Optimistic order clear ──────────────────────────────────────────────
      // Error notifications echo the correlation_id we stamped on the request, so we can resolve
      // the exact submission. Success/lifecycle payloads don't carry it, so those still fall back
      // to the "newest pending submission for this symbol" heuristic.
      const correlationId =
        orderExecutionError?.correlationId ?? orderValidationError?.orderParams?.correlationId
      const notifSymbol =
        orderResponse?.symbol ?? algoLifecycle?.symbol ?? orderValidationError?.symbol

      const pendingRfq = correlationId ? pendingRfqs[correlationId] : undefined
      if (correlationId && pendingRfq && (orderValidationError || orderExecutionError)) {
        const message =
          orderExecutionError?.errorMessage ??
          orderValidationError?.errorMessage ??
          'RFQ submission failed'
        showErrorToast(message, undefined, accountName, exchange)
        // Drop the account-wide popup this submission optimistically opened (if the
        // create response had already resolved it to an rfqId) — the RFQ never
        // actually went live, so there's nothing left to show a quote-accept view for.
        if (pendingRfq.rfqId) {
          const rfqId = pendingRfq.rfqId
          store.set(activeRfqPopupIdsAtom, (prev) => prev.filter((id) => id !== rfqId))
        }
        store.set(pendingRfqOrdersAtom, (prev) => {
          const next = { ...prev }
          delete next[correlationId]
          return next
        })
      }

      let submissionId: string | undefined
      if (correlationId && pendingOrders[correlationId]) {
        submissionId = correlationId
      } else if (notifSymbol) {
        // Composite-keyed by (accountId, symbol) — a bare-symbol fallback would resolve
        // whichever account most recently submitted that symbol, not necessarily the
        // account this notification actually belongs to.
        const candidateIds = pendingSymbols[targetKey(notification.accountId, notifSymbol)] ?? []
        submissionId = candidateIds[candidateIds.length - 1]
      }

      const pending = submissionId ? pendingOrders[submissionId] : undefined
      if (pending && submissionId) {
        const id = submissionId // const for safe capture inside the atom updater closures
        let symbolResult: OptimisticOrderResult | null = null

        if (orderResponse) {
          symbolResult =
            orderResponse.errorMessage || orderResponse.status === OrderStatus.ORDER_STATUS_REJECTED
              ? 'failure'
              : 'success'
        } else if (algoLifecycle) {
          if (algoLifecycle.status === AlgoStatus.ALGO_STATUS_FAILED) {
            symbolResult = 'failure'
          } else if (algoLifecycle.status === AlgoStatus.ALGO_STATUS_COMPLETED) {
            symbolResult = 'success'
          }
          // Intermediate states (STARTED, RUNNING) — keep pending
        } else if (orderValidationError || orderExecutionError) {
          // Pre-trade / algo-start refusal — resolve the optimistic order as failed.
          symbolResult = 'failure'
        }

        if (symbolResult !== null) {
          // A per-symbol notification resolves just that (accountId, symbol) target; an
          // execution error carries no symbol, so it fails every target in the submission.
          const targetKeys = notifSymbol
            ? [targetKey(notification.accountId, notifSymbol)]
            : pending.targets.map((t) => targetKey(t.accountId, t.symbol))
          const updatedResolved = { ...pending.resolved }
          for (const k of targetKeys) updatedResolved[k] = symbolResult
          const allResolved = pending.targets.every(
            (t) => targetKey(t.accountId, t.symbol) in updatedResolved
          )

          if (allResolved) {
            const finalResult: OptimisticOrderResult = Object.values(updatedResolved).includes(
              'failure'
            )
              ? 'failure'
              : 'success'
            store.set(optimisticOrderResultAtomFamily(pending.cardId), {
              submissionId: id,
              result: finalResult,
            })
          } else {
            store.set(pendingOptimisticOrdersAtom, (prev) => ({
              ...prev,
              [id]: { ...(prev[id] ?? pending), resolved: updatedResolved, acknowledged: true },
            }))
          }
        } else if (!pending.acknowledged) {
          // An algo still STARTED/RUNNING: not resolved, but the system did answer.
          store.set(pendingOptimisticOrdersAtom, (prev) =>
            prev[id] ? { ...prev, [id]: { ...prev[id], acknowledged: true } } : prev
          )
        }
      }
      // ───────────────────────────────────────────────────────────────────────

      // Validation / execution errors carry neither an orderResponse nor an algoLifecycle, so
      // the order-update logic below skips them entirely. Surface them as an error toast.
      // showOrderNotificationToast already routes each variant to the right error toast.
      if (orderValidationError || orderExecutionError) {
        showOrderNotificationToast(notification, classified, accountName, exchange)
        if (isFreshForSound) play('error')
        return
      }

      if (algoLifecycle) {
        const { algoId, status } = algoLifecycle
        const isCompleted = status === AlgoStatus.ALGO_STATUS_COMPLETED

        // Reconcile the exact account/algo pair immediately. Retained algo types keep
        // their terminal state for Past/History; ordinary TWAP/Limit Chase rows drop.
        if (TERMINAL_ALGO_STATUSES.has(status) && algoId) {
          setAlgoStates((prev) =>
            reconcileTerminalAlgoState(prev, { accountId: notification.accountId, algoId }, status)
          )
        }

        showAlgoLifecycleToast(classified, accountName, exchange)

        if (isCompleted && settings.tradeSound && isFreshForSound) {
          play('success')
        }
        return
      }

      // Terminal / meaningful order updates get the detailed toast (fill qty, avg price).
      // PENDING and OPEN are just acks for placement — no fill data yet, so they'd render
      // a misleading "Filled: 0 / Avg Price: 0" grid on the detailed card — but the user
      // still wants to know a limit order was placed, so those get a plain compact info
      // toast instead (classified.title is "Order Open"/"Order Pending", no fill grid).
      // (All statuses are still recorded in history above regardless.)
      const status = orderResponse?.status
      const hasErrorMessage = !!orderResponse?.errorMessage
      const shouldShowToast =
        !!orderResponse &&
        (hasErrorMessage ||
          status === OrderStatus.ORDER_STATUS_FILLED ||
          status === OrderStatus.ORDER_STATUS_PARTIALLY_FILLED ||
          status === OrderStatus.ORDER_STATUS_REJECTED ||
          status === OrderStatus.ORDER_STATUS_CANCELLED)
      const shouldShowPlacedToast =
        !!orderResponse &&
        !hasErrorMessage &&
        (status === OrderStatus.ORDER_STATUS_OPEN || status === OrderStatus.ORDER_STATUS_PENDING)
      const shouldSurfaceOrderToast =
        !!orderResponse &&
        (hasErrorMessage ||
          orderNotificationLifecycle.accept(
            notification.accountId,
            orderResponse.orderId,
            orderResponse.status
          ))

      if ((shouldShowToast || shouldShowPlacedToast) && shouldSurfaceOrderToast) {
        if (status === OrderStatus.ORDER_STATUS_OPEN) {
          store.set(activeOrderOpenRequestAtom, {
            id: randomUUID(),
            accountId: notification.accountId,
            orderId: orderResponse.orderId,
          })
        }
        showOrderNotificationToast(notification, classified, accountName, exchange)
      }

      // Play SFX only for successful fills (no error message)
      const isFilled =
        orderResponse &&
        !orderResponse.errorMessage &&
        (orderResponse.status === OrderStatus.ORDER_STATUS_FILLED ||
          orderResponse.status === OrderStatus.ORDER_STATUS_PARTIALLY_FILLED)

      if (isFilled && settings.tradeSound && isFreshForSound) {
        play('success')
      }
    },
    [
      tNotifications,
      settings.tradeSound,
      pendingOrders,
      pendingSymbols,
      pendingRfqs,
      store,
      setAlgoStates,
      accountInfoById,
      orderNotificationLifecycle,
    ]
  )

  useMultiNotificationStream({
    connected: isAuthenticated && isResolved && notificationPayloads.length > 0,
    payloads: notificationPayloads,
    onMessage: handleNotificationMessage,
  })

  return null
}
